import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {BaseControlDirective} from '../../directives/base-control.directive';
import {Observable} from 'rxjs/internal/Observable';
import {SearchInputColorEnum} from '../search-input/search-input.component';
import {Select, Store} from '@ngxs/store';
import {DrugsState} from '../../states/drugs/drugs.state';
import {IDrug} from '../../interfaces/drug.interface';
import {GetInventoryListFromSearch, SetDrugsLoading as SetDrugsLoading} from '../../states/drugs/drugs.actions';
import {IDrugSearchParams} from '../../interfaces/drug-search-params.interface';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AuthState} from '../../states/auth/auth.state';
import {skip} from 'rxjs/operators';
import {IInventory} from '../../interfaces/inventory.interface';
import {InventoryInfoRequest} from '../../interfaces/search-criteria';
import { DrugsService } from '../../services/drugs.service';
import { ProfileState } from '../../states/profile/profile.state';
import { IWholesaler } from '../../interfaces/wholesaler.interface';
import { FilterWholesalers } from '../../states/profile/profile.actions';
import { DrugScheduleEnum } from '../../interfaces/DrugScheduleEnum ';
import { ILocationToExclude } from '../../interfaces/ILocationToExclude.interface';
import { InventoryState } from '../../states/inventory/inventory.state';
import { PharmacyState } from '../../states/pharmacy/pharmacy.state';
import { ICustomDrug } from '../../interfaces/custom-drug-interface';

export enum BarcodeTypeEnum {
  Code128 = 'code_128',
  Code39 = 'code_39',
  Ean8 = 'ean_8',
}

@UntilDestroy()
@Component({
  selector: 'app-input-search-scanner',
  templateUrl: './input-search-scanner.component.html',
  styleUrls: ['./input-search-scanner.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputSearchScannerComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => InputSearchScannerComponent), multi: true},
  ],
})
export class InputSearchScannerComponent extends BaseControlDirective implements OnInit, ControlValueAccessor, AfterViewInit {
  @Input() label: string;
  @Input() placeholder: string;
  @Input() searchResults: any[];
  @Input() loading: boolean;
  @Input() color: SearchInputColorEnum;
  @Input() disable: boolean;
  @Input() autofocus: boolean;
  @Input() isInventory: boolean;
  @Input() errorMessage: string;
  @Input() hasError: boolean;
  @Input() active: boolean;
  @Input() hideDropdown: boolean;
  @Input() barcodeType: BarcodeTypeEnum | BarcodeTypeEnum[] = BarcodeTypeEnum.Code128;
  @Input() searchEmptyInventory: boolean;
  @Input() wholesalerSearch: boolean;
  @Input() includeLastAudit: boolean;
  @Input() searchOnlyC2s: boolean;
  @Input() locationId: string;
  @Input() singleLocationResult: boolean;
  @Input() locationsToExclude: ILocationToExclude[] = [];
  @Input() showLocation: boolean = true;
  @Input() isCustomDrugSearch: boolean;

  @Input() searchBothCustomAndFdaDrugs: boolean;

  @Input() gpi: string = null;

  @Output() optionSelected: EventEmitter<any> = new EventEmitter();
  @Output() searchValueChanged: EventEmitter<string> = new EventEmitter();
  @Output() searchCleared: EventEmitter<void> = new EventEmitter();
  @Output() keydownTab: EventEmitter<void> = new EventEmitter();

  @Select(AuthState.pharmacyId) pharmacyId$: Observable<string | undefined>;
  @Select(DrugsState.loading) searchLoading$: Observable<boolean | undefined>;
  @Select(DrugsState.drugList) drugList$: Observable<IDrug[]>;
  @Select(DrugsState.inventoryList) inventoryList$: Observable<IInventory[]>;
  @Select(ProfileState.currentPharmacyAvailableWholesalersList) wholesalers$: Observable<IWholesaler[]>;
  @Select(DrugsState.customDrugs) customDrugs$: Observable<ICustomDrug[]>;
  
  @ViewChild('appSearch') appSearch;

  innerSearchList: any[] = [];

  barcodeControl: UntypedFormControl;

  processed = false;
  scannerChange: boolean;

  pharmacyID: string | null | undefined;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private drugsService: DrugsService,
    private cd: ChangeDetectorRef
  ) {
    super();

    this.barcodeControl = this.fb.control('');
  }
  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (this.isInventory && !this.hideDropdown) {
      this.inventoryList$.pipe(skip(1), untilDestroyed(this))
        .subscribe((data: IInventory[]) => {
          this.handleInnerSearchList(data);
          this.cd.detectChanges();
        });
    } else if (this.wholesalerSearch) {
      this.wholesalers$.pipe(skip(1), untilDestroyed(this))
      .subscribe((data: IWholesaler[]) => {
        this.handleInnerSearchList(data);
      });
    }
     else {
      this.drugsService.filteredDrugs$.pipe(untilDestroyed(this))
        .subscribe((data: IDrug[]) => {
            setTimeout(() => {
              this.handleInnerSearchList(data);
              this.store.dispatch(new SetDrugsLoading(false));
              this.cd.detectChanges();
            }, 0);  
        });
    }
    this.pharmacyId$.pipe(untilDestroyed(this))
      .subscribe((id: string) => {
        this.pharmacyID = id;
      });
  }

  handleInnerSearchList(data: any): void {
    this.innerSearchList = data || [];
    if (this.innerSearchList.length === 1 && this.scannerChange) {
      this.optionSelect(this.innerSearchList[0]);
    } else if (!this.innerSearchList.length && this.gpi) {
      this.errorMessage = 'No matching equivalents found';
    }

    setTimeout(() => this.processed = true);
  }

  writeValue(value: any): void {
    this.value = value;
    this.barcodeControl.patchValue(value);
    setTimeout(() => this.searchValueChanged.emit(value));
  }

  searchClear(): void {
    this.writeValue('');
    this.searchCleared.emit();
  }

  optionSelect(option: any): void {
    this.optionSelected.emit(option);
  }

  handleSearchInput(token: string): void {
    // get searchResults on init and provide to the component as search array
    // OR use an inner source innerSearchList to search for a drug by product ndc or name, or scanner
    if (!this.searchResults) {
      if (this.isInventory && !this.hideDropdown) {
        const iparams = new InventoryInfoRequest();
        iparams.pharmacyID = this.pharmacyID;
        iparams.token = token;
        iparams.limitToPharmacy = this.isInventory;
        iparams.active = this.active;
        iparams.searchEmptyInventory = this.searchEmptyInventory;
        iparams.withLastAudit = this.includeLastAudit;
        iparams.schedules = this.searchOnlyC2s ? [DrugScheduleEnum.C2] : null;
        iparams.locationId = this.locationId;
        iparams.singleLocationResult = this.singleLocationResult;
        iparams.locationsToExclude = this.locationsToExclude;
        iparams.gpi = this.gpi;
        this.store.dispatch(new GetInventoryListFromSearch(iparams));
      }
      else if(this.wholesalerSearch) {
        this.hideDropdown = false;
        this.store.dispatch(new FilterWholesalers(token));
      }  else if(this.isCustomDrugSearch) {
        this.store.dispatch(new SetDrugsLoading(true));
        this.drugsService.getCustomDrugsListFromSearch(token);
      } 
      else {
        const dparams: IDrugSearchParams = {
          pharmacyID: this.pharmacyID,
          token,
          skip: 0,
          active: true,
          limitToPharmacy: this.isInventory
        };
        if (!this.hideDropdown) {
          if (!token || token?.match('^$') || (this.innerSearchList?.length === 1 && this.innerSearchList[0]?.friendlyName === token)) 
              return;

          this.store.dispatch(new SetDrugsLoading(true));
          this.drugsService.getDrugListFromSearch(dparams.token, this.searchBothCustomAndFdaDrugs);
        }
      }
    }
    this.writeValue(token);

    this.processed = false;
  }

  resetSearch() {
    this.appSearch?.clearSearch();
  }

}
